<template>
    <div id="multi-content">
        <div class="d-flex justify-content-center p-3">
            <b-spinner v-if="loading" style="width: 2rem; height: 2rem" label="Large Spinner" type="grow" />
        </div>
        <b-tabs card v-model="tabIndex">
            <b-tab v-for="code in systemLanguages" :key="code" :title="!!code ? code.toUpperCase() : ''">
                <div v-for="(c, i) of content" :key="`${code}-${i}`" class="relative">
                    <b-form-group
                        :label="c.label"
                        class="exclusive-text-tool-class"
                        :class="{ 'exclusive-text-tool-hided-class': hideTextCustomization }"
                    >
                        <b-input-group class="position-relative translatable-area">
                            <b-input
                                :name="`${c.label}-${code}`"
                                :placeholder="c.placeholder"
                                v-if="c.type === 'input'"
                                v-validate="c.validation"
                                v-model="content[i].data[code]"
                            />
                            <b-textarea
                                :name="`${c.label}-${code}`"
                                :placeholder="c.placeholder"
                                v-if="c.type === 'text'"
                                v-validate="c.validation"
                                v-model="content[i].data[code]"
                            />
                            <wysiwyg
                                :name="`${c.label}-${code}`"
                                :placeholder="c.placeholder"
                                v-if="c.type === 'rich'"
                                v-validate="c.validation"
                                v-model="content[i].data[code]"
                            />
                            <div class="translate-toolbox">
                                <button
                                    @click.prevent="translate(i, code)"
                                    class="translate-btn mr-1"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="$t('message.vtippyTranslateButton')"
                                >
                                    <md-icon>translate</md-icon>
                                </button>
                                <button
                                    @click.prevent="clearContents(i, code)"
                                    class="translate-btn bg-danger"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="$t('message.vtippyTranslateClearButton')"
                                >
                                    <md-icon>clear</md-icon>
                                </button>
                            </div>
                        </b-input-group>
                    </b-form-group>
                </div>
                <div
                    v-if="systemLanguages.length > 1"
                    class="delete-lang-button"
                    @click="showDeleteModal(code)"
                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                    :content="$t('message.deleteSystemLanguage', { code })"
                >
                    <md-icon class="delete-icon" color="red">delete</md-icon>
                </div>
            </b-tab>
            <template #tabs-end>
                <b-nav-item role="presentation" @click.prevent="showAddingNewLanguageSelectionForm()"
                    ><b>+</b></b-nav-item
                >
            </template>
        </b-tabs>
        <Modal2
            v-if="isModalShow"
            id="lang-form"
            :title="$t('message.selectALanguage')"
            :show-footer="false"
            @cancel="hideModal"
        >
            <b-form class="d-flex flex-column justify-content-center" @submit.prevent="addLang">
                <b-row>
                    <b-col md="12">
                        <b-form-group :label="$t('message.language')">
                            <b-form-select v-model="lang">
                                <option :key="l.code" v-for="l in filteredLanguages" :value="l.code">
                                    {{ l.label }}
                                </option>
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="12">
                        <b-button type="submit" variant="primary" :disabled="typeof lang !== 'string'">
                            <template>
                                {{ $t("message.save") }}
                            </template>
                        </b-button>
                        <b-button @click="hideModal">
                            {{ $t("message.cancel") }}
                        </b-button>
                    </b-col>
                </b-row>
            </b-form>
        </Modal2>
    </div>
</template>

<script>
import languages from "../language/languages.json";
import log2 from "../utils/log2";
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import { sendSocketMessage } from "../utils/socketStream";
import request from "../utils/request";
import Modal2 from "../components/common/Modal2.vue";

const log = log2("MultiContent");
export default {
    name: "MultiContent",
    components: {
        Modal2,
    },
    props: {
        /**
         * {
         *      type: 'input' | 'text' | 'rich',
         *      field: 'string',
         *      label: 'string',
         *      placeholder: 'string',
         *      data: {
         *          [langCode: string]: string
         *      },
         *      validation: 'required',
         * }
         */
        value: Array,
        hideTextCustomization: {
            type: Boolean,
            default: false,
            required: false,
        },
    },
    computed: {
        ...mapState({
            systemLanguages: (state) => state.setting.settings.systemLanguages,
            site: (state) => state.site.site,
        }),
        filteredLanguages() {
            return languages.filter((x) => !this.systemLanguages.includes(x.code));
        },
    },
    data() {
        return {
            isModalShow: false,
            lang: null,
            loading: false,
            tabIndex: 0,
            content: [],
            imgCaches: {},
        };
    },
    methods: {
        ...mapActions({
            updateSettings: "setting/updateSettings",
            setSettingValue: "setting/setSettingValue",
        }),
        showModal() {
            this.isModalShow = true;
        },
        hideModal() {
            this.isModalShow = false;
        },
        async translate(index, code) {
            const text = this.clearImages(this.content[index].data[code]);
            log("translate", {
                text,
            });
            this.loading = true;
            try {
                if (!!text) {
                    let destinationLanguages = this.systemLanguages.filter((x) => !this.content[index].data[x]);
                    const translates = await Promise.all(
                        destinationLanguages.map(async (to) => {
                            const { data } = await request.post("/translate", {
                                data: text,
                                from: code,
                                to,
                            });
                            return { to, data };
                        })
                    );
                    log("response", translates);
                    if (!!translates.length) {
                        for (const translate of translates) {
                            this.content[index].data[translate.to] = this.reWriteImages(translate.data);
                        }
                    }
                    this.$emit("input", this.content);
                }
            } catch (e) {
                log.error(e);
            }
            this.loading = false;
        },
        showAddingNewLanguageSelectionForm() {
            this.lang = null;
            /*this.$bvModal.show("lang-form");*/
            this.showModal();
        },
        clearImages(htmlStr) {
            const parser = new DOMParser();
            const htmlDom = parser.parseFromString(htmlStr, "text/html");
            htmlDom.body.querySelectorAll("img").forEach((img, index) => {
                this.imgCaches[index] = img.src;
                img.src = "";
                img.setAttribute("cache-index", index.toString());
            });
            return htmlDom.body.innerHTML;
        },

        reWriteImages(htmlStr) {
            const parser = new DOMParser();
            const htmlDom = parser.parseFromString(htmlStr, "text/html");
            htmlDom.body.querySelectorAll("img").forEach((img) => {
                img.src = this.imgCaches[img.getAttribute("cache-index")];
            });
            return htmlDom.body.innerHTML;
        },

        clearContents(index, code) {
            const others = Object.keys(this.content[index].data).filter((lang) => lang !== code);
            for (const other of others) {
                this.content[index].data[other] = "";
            }
        },

        addLang() {
            log("addLang", {});
            const newSettings = this.setSettingValue({
                setting: "systemLanguages",
                value: [...this.systemLanguages, this.lang],
            });
            if (newSettings.then) newSettings.then((x) => this.handleSave(x));
            else this.handleSave(newSettings);
            /*this.$bvModal.hide("lang-form");*/
            this.hideModal();
        },
        deleteLang(langCode) {
            log("deleteLang", {});
            const newSettings = this.setSettingValue({
                setting: "systemLanguages",
                value: this.systemLanguages.filter((lang) => lang !== langCode),
            });
            if (newSettings.then) newSettings.then((x) => this.handleSave(x));
            else this.handleSave(newSettings);
            this.tabIndex = this.systemLanguages.length;
        },
        showDeleteModal(langCode) {
            log("showDeleteModal", {});
            if (!!langCode)
                this.$dialog
                    .confirm(this.$t("message.languageRemovalQuestion", { code: langCode.toUpperCase() }), {
                        okText: this.$t("message.continue"),
                        cancelText: this.$t("message.cancel"),
                    })
                    .then(() => this.deleteLang(langCode));
            else this.deleteLang(langCode);
        },
        handleSave: _.debounce(function (newSettings) {
            this.updateSettings(newSettings).then(() => {
                sendSocketMessage("admin settings-updated", {
                    siteId: this.site._id,
                });
                this.$toasted.success(this.$t("message.settingsSaved"), {
                    duration: 1000,
                    position: "bottom-center",
                });
            });
        }, 300),
        async validate() {
            try {
                return await this.$validator.validateAll();
            } catch (e) {
                log.error(e);
                return false;
            }
        },
    },
    mounted() {
        log("mounted");
        this.content = this.value.map((c) => {
            for (const systemLanguage of this.systemLanguages) if (!c.data[systemLanguage]) c.data[systemLanguage] = "";
            return c;
        });
    },
    watch: {
        content: {
            deep: true,
            handler: _.debounce(function (nV, oV) {
                log("watch content");
                this.$emit("input", this.content);
            }, 0),
        },
    },
};
</script>
<style lang="scss">
#multi-content {
    .exclusive-text-tool-class {
        .editr--toolbar {
            display: flex;
        }
    }

    .exclusive-text-tool-hided-class {
        .editr--toolbar {
            display: none !important;
        }
    }
}
</style>

<style lang="scss" scoped>
#multi-content {
    .card-body {
        position: relative;

        .delete-lang-button {
            position: absolute;
            top: -39px;
            right: 11px;
            border: none;
            background-color: #cd2942;
            padding: 0.3rem;
            border-radius: 0.3rem;
            width: auto;
            cursor: pointer;

            .delete-icon {
                color: #ffffff;
                font-size: 20px !important;
            }
        }

        .translate-btn {
            border: none;
            background-color: #29afcd;
            padding: 0.3rem;
            border-radius: 0.3rem;
            width: auto;
            height: 30px;

            i {
                color: #ffffff;
                font-size: 20px !important;
            }
        }
    }

    .translatable-area {
        .translate-toolbox {
            position: absolute;
            right: 0;
            top: 0;
            opacity: 0;
            transition: all 0.3s;
        }

        &:hover .translate-toolbox {
            opacity: 1;
        }
    }
}
</style>
<style lang="scss">
#lang-form {
    .ant-modal {
        width: 630px !important;
    }
}
</style>
